<template>
  <page
    v-model:filter="q"
    :name="$t('I tuoi prodotti')"
    :title="$t('I tuoi prodotti')"
    :empty="productOrder.length == 0"
    :state="[paymentsState, productOrderState]"
    sticky
  >
    <template #toolbar-pre>
      <pf-toolbar-item ref="toolbar">
        <pf-dropdown :text="$t('Raggruppa per') + ': ' + groupByOption[groupBy]">
          <pf-dropdown-item v-for="(value, key) in groupByOption" :key="key" @click.prevent="groupBy = key">
            {{ value }}
            <template #icon v-if="key === groupBy">
              <check-icon  />
            </template>
          </pf-dropdown-item>
        </pf-dropdown>
      </pf-toolbar-item>
    </template>

    <pf-page-section v-for="(_rgroup, group) of groupedProducts" :key="group">
      <pf-title h="2" v-if="groupBy == 'renewal_date'">
        <template v-if="group == 'test'">{{ $t('Prodotti in prova') }}</template>
        <template v-else-if="group == 'canceled'">{{ $t('Prodotti scaduti da più di {0} giorni', [retentionDays]) }}</template>
        <template v-else-if="group == 'gracePeriod'">{{ $t('Prodotti scaduti da meno di {0} giorni', [retentionDays]) }}</template>
        <template v-else-if="group == 'scadenza3'">{{ $t('Prodotti che scadranno nei prossimi tre mesi') }}</template>
        <template v-else-if="group == 'scadenza12'">{{ $t('Prodotti che scadranno entro fine anno') }}</template>
        <template v-else-if="group == 'scadenzaFuture'">{{ $t('Altri con scadenza futura') }}</template>
        <template v-else>{{ $t('In scadenza entro fine mese') }}</template>
      </pf-title>
      <pf-title v-else>{{ group }}</pf-title>

      <product-list
        v-if="groupedProducts[group].length"
        :products="groupedProducts[group]"
        :payments="payments"
        @submit="productOrderState.update"
        :hide-domain-sort="groupBy === 'domain'"
        :filter="q"
        :ouia-id="`product-table-${group}`"
        />
    </pf-page-section>
  </page>
</template>

<style scoped lang="scss">
.price {
  text-align: right;
}
:deep(button.pf-v6-c-button), :deep(a.pf-v6-c-button) {
  margin-right: 5px;
}
</style>

<script lang="ts" setup>
import { ref, type Ref, computed } from 'vue';
import { asyncComputed, asyncComputedList } from '@common/asyncComputed';
import ProductList from '@/components/product-list.vue';
import CheckIcon from '@vue-patternfly/icons/check-icon';
import { type ProductOrder, ProductOrdersResource, type ProductsExpiringPayments, ProductsExpiringPaymentsResource } from '@/resources';
import { DateTime, Interval } from 'luxon';
import { parseDateTime } from '@common/date';
import { $t as _t } from '@/i18n';

type GroupByOption = 'renewal_date' | 'domain' | 'customer_note';
const groupByOption: Record<GroupByOption, string> = {
  'renewal_date': _t('Data di rinnovo'),
  'domain': _t('Nome a dominio'),
  'customer_note': _t('Note'),
};

const { state: productOrderState, result: productOrder } = asyncComputedList(() => new ProductOrdersResource().get());
const { state: paymentsState, result: payments } = asyncComputed(() => new ProductsExpiringPaymentsResource().get(), {} as Record<number, ProductsExpiringPayments>);

const now = DateTime.now().startOf('day');

const q = ref(''); // search query
const groupBy: Ref<GroupByOption> = ref('renewal_date');

const retentionDays = 10;
const endOfMonth = now.endOf('month');
const endOfYear = now.endOf('year');
const nextMonth = now.startOf('month').plus({ months: 1 });
const threeMonthsFuture = now.endOf('month').plus({ months: 3 });

function groupProductsByRenewalDate() {
  const grouped = {
    test: [] as ProductOrder[],
    canceled: [] as ProductOrder[],
    gracePeriod: [] as ProductOrder[],
    scadenza: [] as ProductOrder[],
    scadenza3: [] as ProductOrder[],
    scadenza12: [] as ProductOrder[],
    scadenzaFuture: [] as ProductOrder[],
  };

  for (const product of productOrder.value) {
    const dataFine = parseDateTime(product.data_fine);

    if (product.free_period > 0) {
      grouped.test.push(product);
      continue;
    }

    if (product.grace_period) {
      grouped.gracePeriod.push(product);
      continue;
    }

    if (product.days_to_expiration < 0) {
      grouped.canceled.push(product);
      continue;
    }

    // fine mese
    if (dataFine <= endOfMonth) {
      grouped.scadenza.push(product);
      continue;
    }
    // prossimi tre mesi
    if (Interval.fromDateTimes(nextMonth, threeMonthsFuture).contains(dataFine)) {
      grouped.scadenza3.push(product);
      continue;
    }

    // fine anno
    if (dataFine <= endOfYear) {
      grouped.scadenza12.push(product);
      continue;
    }

    // altri
    grouped.scadenzaFuture.push(product);
  }

  // unset empty array keys
  for (const k of Object.keys(grouped) as (keyof typeof grouped)[]) {
    if (grouped[k].length == 0) {
      Reflect.deleteProperty(grouped, k);
    }
  }

  return grouped;
}

function groupProductsByDomain() {
  const grouped: Record<string, ProductOrder[]> = {};

  let dominio = '';
  for (const product of productOrder.value) {
    dominio = product.dominio ?? '';
    if (!grouped[dominio]) {
      grouped[dominio] = [product];
    } else {
      grouped[dominio].push(product);
    }
  }

  return Object.fromEntries(Object.entries(grouped).sort());
}

function groupProductsByCustomerNote() {
  const grouped: Record<string, ProductOrder[]> = {};

  for (const product of productOrder.value) {
    if (!grouped[product.note_cliente ?? _t('Senza note')]) {
      grouped[product.note_cliente ?? _t('Senza note')] = [product];
    } else {
      grouped[product.note_cliente ?? _t('Senza note')].push(product);
    }
  }

  return Object.fromEntries(Object.entries(grouped).sort());
}

const groupedProducts = computed(() => {
  if (groupBy.value === 'domain') {
    return groupProductsByDomain();
  }

  if (groupBy.value === 'customer_note') {
    return groupProductsByCustomerNote();
  }

  return groupProductsByRenewalDate();
});
</script>
